import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconKey } from '../../../../whitelabel/icon-key.enum';
import { LanguageService } from '../../../core/services/language.service';
import { MessagesComponent } from '../../../osp-ui/components/messages/messages/messages.component';
import { SpinnerComponent } from '../../../osp-ui/components/misc/spinner/spinner.component';
import { DialogComponent } from '../../../osp-ui/components/overlay/dialog/dialog/dialog.component';
import { OspTemplateDirective } from '../../../osp-ui/directives/osp-template.directive';
import { ActionOption } from '../../contract/action-option.contract';
import { IconPipe } from '../../pipes/icon.pipe';
import { MigrationDataService, MigrationStatus } from '../../services/migration-data.service';
import { ActionPanelComponent } from '../action-panel/action-panel.component';

enum MigrationDialogPhase {
  BEFORE_MIGRATION = 'BEFORE_MIGRATION',
  MIGRATION_IN_PROGRESS = 'MIGRATION_IN_PROGRESS',
  AFTER_MIGRATION = 'AFTER_MIGRATION',
}

export type MigrationDataDialogData = {
  onInit: boolean,
};

@Component({
  selector: 'osp-migration-data-dialog',
  standalone: true,
  imports: [
    OspTemplateDirective,
    DialogComponent,
    ActionPanelComponent,
    TranslateModule,
    NgTemplateOutlet,
    SpinnerComponent,
    NgClass,
    IconPipe,
    MessagesComponent,
  ],
  templateUrl: './migration-data-dialog.component.html',
  styleUrl: './migration-data-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MigrationDataDialogComponent {
  private dialogRef = inject(DynamicDialogRef);
  private destroyRef = inject(DestroyRef);
  private languageService = inject(LanguageService);
  private migrationDataService = inject(MigrationDataService);
  private dialogData: MigrationDataDialogData = inject(DynamicDialogConfig).data;

  private actionOptionsBeforePhaseOnInit: ActionOption[] = [
    {
      position: 'left',
      label: this.translate('revolution.shared.migration.migrationDialog.doNotStartDoNotAskAgain'),
      action: () => this.cancelMigration(true),
      category: 'danger',
    },
    {
      position: 'right',
      label: this.translate('revolution.shared.migration.migrationDialog.doNotStartAskAgain'),
      action: () => this.cancelMigration(),
      category: 'secondary',
    },
    {
      position: 'right',
      label: this.translate('general.buttons.yes'),
      action: () => this.startMigration(),
      category: 'primary',
    },
  ];
  private actionOptionsBeforePhaseDefault: ActionOption[] = [
    {
      position: 'right',
      label: this.translate('general.buttons.cancel'),
      action: () => this.cancelMigration(),
      category: 'secondary',
    },
    {
      position: 'right',
      label: this.translate('general.buttons.yes'),
      action: () => this.startMigration(),
      category: 'primary',
    },
  ];
  private actionOptionsAfterPhase: ActionOption[] = [{
    position: 'right',
    label: this.translate('general.buttons.ok'),
    action: () => this.dialogRef.close(),
    category: 'primary',
  }];

  protected IconKey = IconKey;
  protected MigrationDialogPhase = MigrationDialogPhase;

  private $migrationInProgress = signal(false);
  private $migrationResult = signal<MigrationStatus | null>(null);
  protected $activePhase = computed<MigrationDialogPhase>(() => {
    if (this.$migrationInProgress()) return MigrationDialogPhase.MIGRATION_IN_PROGRESS;
    return this.$migrationResult()
      ? MigrationDialogPhase.AFTER_MIGRATION
      : MigrationDialogPhase.BEFORE_MIGRATION;
  });
  protected $actionOptions = computed<ActionOption[] | null>(() => {
    if (this.$activePhase() === MigrationDialogPhase.BEFORE_MIGRATION) {
      return this.getBeforePhaseActionOptions();
    } else if (this.$activePhase() === MigrationDialogPhase.AFTER_MIGRATION) {
      return this.getAfterPhaseActionOptions();
    }
    return null;
  });
  protected $migrationResultFormatted = computed(() => {
    const details = this.$migrationResult()?.migrationDetails;
    if (!details) return null;

    return [
      {
        label: this.translate('revolution.shared.migration.migrationDialog.migrationResultLabelAlarms'),
        success: details.classicAlarmsSuccess,
        failure: details.classicAlarmsFailure,
      },
      {
        label: this.translate('revolution.shared.migration.migrationDialog.migrationResultLabelCustomEvents'),
        success: details.customEventsSuccess,
        failure: details.customEventsFailure,
      },
      {
        label: this.translate('revolution.shared.migration.migrationDialog.migrationResultLabelAutomaticReports'),
        success: details.reportJobsSuccess,
        failure: details.reportJobsFailure,
      },
    ]
  });
  protected $showFailureHint = computed(() => this.$migrationResultFormatted()?.some(({ failure }) => failure > 0));

  private getBeforePhaseActionOptions(): ActionOption[] {
    return this.dialogData.onInit ? this.actionOptionsBeforePhaseOnInit : this.actionOptionsBeforePhaseDefault;
  }

  private getAfterPhaseActionOptions(): ActionOption[] {
    return this.actionOptionsAfterPhase;
  }

  private cancelMigration(doNotAskAgain?: boolean): void {
    if (doNotAskAgain) {
      this.migrationDataService.setDoNotAskAgainPromptOnInit(true);
    }
    this.dialogRef.close();
  }

  private startMigration(): void {
    this.$migrationInProgress.set(true);
    this.migrationDataService.startMigration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status) => {
          this.$migrationResult.set(status);
          this.$migrationInProgress.set(false);
        },
        error: () => this.$migrationInProgress.set(false),
      });
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
